// Overview tab inside the MU drill-down. Editable metadata (description,
// comments, dependsOn) + a compact feature list with scoring chips +
// rolled-up effort total.

import React, { useState } from 'react';
import { Pencil, Save, X, Check } from 'lucide-react';
import type {
  DiscoveryFeature,
  DeliveryAnalysisPayload,
  DeliveryMutation,
  MigrationUnit,
} from '../hooks/sessionTypes';
import { muColor, scoringFor } from '../utils/deliveryAnalysisModel';

interface Props {
  mu: MigrationUnit;
  features: DiscoveryFeature[];
  payload: DeliveryAnalysisPayload;
  allMuNames: string[];
  onPatch: (m: DeliveryMutation) => Promise<void>;
}

const MigrationUnitOverview: React.FC<Props> = ({ mu, features, payload, allMuNames, onPatch }) => {
  const [descDraft, setDescDraft] = useState(mu.description);
  const [editingDesc, setEditingDesc] = useState(false);
  const [commentsDraft, setCommentsDraft] = useState(mu.comments);
  const [editingComments, setEditingComments] = useState(false);

  // Keep drafts in sync when the underlying MU updates externally.
  React.useEffect(() => { if (!editingDesc) setDescDraft(mu.description); }, [mu.description, editingDesc]);
  React.useEffect(() => { if (!editingComments) setCommentsDraft(mu.comments); }, [mu.comments, editingComments]);

  const saveDesc = async () => {
    setEditingDesc(false);
    if (descDraft === mu.description) return;
    await onPatch({ type: 'updateMuDescription', name: mu.name, description: descDraft });
  };
  const saveComments = async () => {
    setEditingComments(false);
    if (commentsDraft === mu.comments) return;
    await onPatch({ type: 'updateMuComments', name: mu.name, comments: commentsDraft });
  };

  const toggleDep = async (depName: string) => {
    const cur = new Set(mu.dependsOn);
    if (cur.has(depName)) cur.delete(depName);
    else cur.add(depName);
    await onPatch({ type: 'setMuDependsOn', name: mu.name, dependsOn: [...cur] });
  };

  const totalEffort = features
    .filter((f) => !payload.hidden[f.candidateId])
    .reduce((s, f) => s + (scoringFor(payload, f.candidateId).effort || 0), 0);

  const eligibleDepNames = allMuNames.filter((n) => n !== mu.name);

  return (
    <div className="mu-tab-body mu-overview">
      <section className="mu-card">
        <header className="mu-card-head">
          <h2>Description</h2>
          {!editingDesc && (
            <button type="button" className="mu-icon-btn" onClick={() => { setDescDraft(mu.description); setEditingDesc(true); }}>
              <Pencil size={12} /> Edit
            </button>
          )}
        </header>
        {editingDesc ? (
          <div className="mu-editor-row">
            <textarea
              className="mu-editor"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              autoFocus
              maxLength={240}
              placeholder="What ships in this MU and why."
            />
            <div className="mu-editor-actions">
              <button type="button" className="dv-btn dv-btn--ghost" onClick={() => setEditingDesc(false)}>
                <X size={12} /> Cancel
              </button>
              <button type="button" className="dv-btn dv-btn--primary" onClick={saveDesc}>
                <Save size={12} /> Save
              </button>
            </div>
          </div>
        ) : (
          <p className="mu-prose">{mu.description || <span className="mu-prose-empty">No description yet.</span>}</p>
        )}
      </section>

      <section className="mu-card">
        <header className="mu-card-head">
          <h2>Comments</h2>
          {!editingComments && (
            <button type="button" className="mu-icon-btn" onClick={() => { setCommentsDraft(mu.comments); setEditingComments(true); }}>
              <Pencil size={12} /> Edit
            </button>
          )}
        </header>
        {editingComments ? (
          <div className="mu-editor-row">
            <textarea
              className="mu-editor mu-editor--tall"
              value={commentsDraft}
              onChange={(e) => setCommentsDraft(e.target.value)}
              autoFocus
              maxLength={2048}
              placeholder="Engineering notes, decisions, caveats — anything that should travel with this MU."
            />
            <div className="mu-editor-actions">
              <button type="button" className="dv-btn dv-btn--ghost" onClick={() => setEditingComments(false)}>
                <X size={12} /> Cancel
              </button>
              <button type="button" className="dv-btn dv-btn--primary" onClick={saveComments}>
                <Save size={12} /> Save
              </button>
            </div>
          </div>
        ) : (
          <p className="mu-prose">{mu.comments || <span className="mu-prose-empty">No comments yet.</span>}</p>
        )}
      </section>

      <section className="mu-card">
        <header className="mu-card-head">
          <h2>Depends on</h2>
        </header>
        {eligibleDepNames.length === 0 ? (
          <p className="mu-prose-empty">No other MUs to depend on yet.</p>
        ) : (
          <div className="mu-deps">
            {eligibleDepNames.map((dep) => {
              const active = mu.dependsOn.includes(dep);
              return (
                <button
                  type="button"
                  key={dep}
                  className={`mu-dep ${active ? 'mu-dep--active' : ''}`}
                  style={active ? { borderColor: muColor(dep), color: muColor(dep), background: `${muColor(dep)}12` } : undefined}
                  onClick={() => toggleDep(dep)}
                >
                  {active && <Check size={10} />}
                  {dep}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="mu-card">
        <header className="mu-card-head">
          <h2>Features ({features.length})</h2>
          <span className="mu-rollup">{totalEffort > 0 ? `${totalEffort} pts total` : 'no effort scored'}</span>
        </header>
        {features.length === 0 ? (
          <p className="mu-prose-empty">No features in this MU.</p>
        ) : (
          <ul className="mu-feature-list">
            {features.map((f) => {
              const s = scoringFor(payload, f.candidateId);
              return (
                <li key={f.candidateId} className="mu-feature-row">
                  <div className="mu-feature-name">
                    <span>{f.name}</span>
                    {f.description && <span className="mu-feature-desc">— {f.description}</span>}
                  </div>
                  <div className="mu-feature-chips">
                    {s.effort != null && <span className="mu-tiny-chip">{s.effort} pts</span>}
                    {s.value && <span className={`mu-tiny-chip mu-tiny-chip--${s.value}`}>{s.value}</span>}
                    {s.tags?.map((t) => <span key={t} className="mu-tiny-chip mu-tiny-chip--tag">{t}</span>)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default MigrationUnitOverview;
